import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import useGET from "../../services/useGET";
import { ImageHandler } from "../../hooks/ImageHandler";
import { getToken } from "../../services/tokenGET";
import AdminSidebar from "../../components/layout/AdminSidebar";
import QuestionForm from "../../components/admin/quiz/QuestionForm";
import { useQuizData } from "../../hooks/admin/quiz/useQuizData";
import { useDropdownState } from "../../hooks/admin/useDropdownState";

export default function AdminQuizPage() {
    const { data: questions } = useGET(`${process.env.REACT_APP_API_URL}/api/questions`);
    const { data: species } = useGET(`${process.env.REACT_APP_API_URL}/api/species`);
    
    const [speciesList, setSpeciesList] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredQuestions, setFilteredQuestions] = useState([]);
    const [speciesSearchQuery, setSpeciesSearchQuery] = useState("");
    
    const speciesDropdown = useDropdownState();
    const quizData = useQuizData(questions, speciesList);

    const {
        handleFileChange: handleFileChangeQ,
        imageFileEncoded: imageFileEncodedQ,
        setImageFileEncoded: setImageFileEncodedQ,
    } = ImageHandler();
    const { handleFileChange: handleFileChange1, imageFileEncoded: imageFileEncoded1 } = ImageHandler();
    const { handleFileChange: handleFileChange2, imageFileEncoded: imageFileEncoded2 } = ImageHandler();
    const { handleFileChange: handleFileChange3, imageFileEncoded: imageFileEncoded3 } = ImageHandler();

    useEffect(() => {
        if (species) {
            setSpeciesList(species);
        }
    }, [species]);

    // Initialize filtered questions
    useEffect(() => {
        setFilteredQuestions(quizData.questionList);
    }, [quizData.questionList]);

    // Filter questions based on search query
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredQuestions(quizData.questionList);
        } else {
            const query = searchQuery.toLowerCase();
            const filtered = quizData.questionList.filter(question => 
                question.question.toLowerCase().includes(query) ||
                question.correct_answer.toLowerCase().includes(query)
            );
            setFilteredQuestions(filtered);
        }
    }, [searchQuery, quizData.questionList]);

    const handleClick = useCallback((selected) => {
        quizData.handleSelectQuestion(selected);
        setImageFileEncodedQ(selected.image);
        // Set the species name in the search box
        const selectedSpeciesObj = speciesList?.find(sp => sp.id === selected.species_id);
        setSpeciesSearchQuery(selectedSpeciesObj?.name || "");
    }, [quizData, setImageFileEncodedQ, speciesList]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    // Filter species based on search query
    const filteredSpecies = speciesList?.filter(sp => 
        sp.name.toLowerCase().includes(speciesSearchQuery.toLowerCase())
    ) || [];

    function handleCheck(id) {
        quizData.setSelectedSpecies(id);
        speciesDropdown.setIsOpen(false);
        // Find the species name for the selected ID
        const selectedSpeciesObj = speciesList?.find(sp => sp.id === parseInt(id));
        setSpeciesSearchQuery(selectedSpeciesObj?.name || "");
    }

    const handleAdd = () => {
        quizData.handleResetForm();
        setImageFileEncodedQ(null);
        setSpeciesSearchQuery("");
        speciesDropdown.setIsOpen(false);
    };

    const handleSave = async () => {
        await quizData.handleSaveQuestion(imageFileEncoded1, imageFileEncoded2, imageFileEncoded3, imageFileEncodedQ);
        setImageFileEncodedQ(null);
    };

    return (
        <div className="flex min-h-screen bg-gray-50 font-body">
            {/* Admin Sidebar */}
            <AdminSidebar />

            {/* Main Content - offset by sidebar width on desktop only */}
            <div className="flex-1 md:ml-64 flex flex-col">
                <main className="flex-grow">
                    {/* Header Section */}
                    <div className="bg-gradient-to-r from-forest to-leaf py-12">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="text-center">
                                <h1 className="font-heading text-4xl font-bold text-white sm:text-5xl">
                                    Redigera Quizfrågor
                                </h1>
                                <p className="mt-4 text-lg text-white/90">
                                    Hantera frågor och svar
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Content Grid */}
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Left Panel - Questions List */}
                            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                                <div className="p-6 border-b border-gray-200">
                                    <h2 className="font-heading text-2xl font-bold text-charcoal mb-4">
                                        Frågelista
                                    </h2>
                                    
                                    {/* Search Bar */}
                                    <div className="relative mb-4">
                                        <input
                                            type="text"
                                            placeholder="Sök frågor..."
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                            className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent"
                                        />
                                        <svg 
                                            className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" 
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>

                                    {/* Add New Button */}
                                    <button
                                        onClick={handleAdd}
                                        className="w-full bg-forest hover:bg-leaf text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center"
                                    >
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        Lägg till ny fråga
                                    </button>
                                </div>
                                
                                <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
                                    {filteredQuestions && filteredQuestions.length > 0 ? (
                                        <div className="p-4 space-y-2">
                                            {filteredQuestions.map((question) => (
                                                <div
                                                    key={question.id}
                                                    onClick={() => {
                                                        handleClick(question);
                                                    }}
                                                    className={`p-4 rounded-lg cursor-pointer transition-all duration-200 border-2 ${
                                                        quizData.selectedItem?.id === question.id
                                                            ? 'bg-sage border-forest shadow-md'
                                                            : 'bg-white border-gray-200 hover:border-leaf hover:shadow-sm'
                                                    }`}
                                                >
                                                    <div className="flex justify-between items-center">
                                                        <div className="flex-1">
                                                            <p className="font-semibold text-charcoal text-sm">{question.question}</p>
                                                            <p className="text-xs text-green-600 mt-1">✓ {question.correct_answer}</p>
                                                        </div>
                                                        {quizData.selectedItem?.id === question.id && (
                                                            <svg className="w-6 h-6 text-forest ml-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="p-6 text-center text-gray-500">
                                            <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <p className="font-semibold text-gray-700 mb-1">Inga frågor hittades</p>
                                            <p className="text-sm text-gray-500">
                                                {searchQuery ? "Prova att söka efter något annat" : "Lägg till en ny fråga för att komma igång"}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right Panel - Edit Question */}
                            <QuestionForm
                                questionText={quizData.questionText}
                                setQuestionText={quizData.setQuestionText}
                                selectedSpecies={quizData.selectedSpecies}
                                setSelectedSpecies={quizData.setSelectedSpecies}
                                speciesSearchQuery={speciesSearchQuery}
                                setSpeciesSearchQuery={setSpeciesSearchQuery}
                                isSpeciesDropdownOpen={speciesDropdown.isOpen}
                                setIsSpeciesDropdownOpen={speciesDropdown.setIsOpen}
                                filteredSpecies={filteredSpecies}
                                handleCheck={handleCheck}
                                speciesDropdownRef={speciesDropdown.dropdownRef}
                                answer1={quizData.answer1}
                                setAnswer1={quizData.setAnswer1}
                                answer2={quizData.answer2}
                                setAnswer2={quizData.setAnswer2}
                                answer3={quizData.answer3}
                                setAnswer3={quizData.setAnswer3}
                                handleRemoveImage={quizData.handleRemoveImage}
                                handleFileChange1={handleFileChange1}
                                handleFileChange2={handleFileChange2}
                                handleFileChange3={handleFileChange3}
                                handleAudioFileChange={quizData.handleAudioFileChange}
                                audioFile={quizData.audioFile}
                                selectedItem={quizData.selectedItem}
                                handleSave={handleSave}
                                handleDeleteQuestion={quizData.handleDeleteQuestion}
                                handleDeleteQuestionImage={quizData.handleDeleteQuestionImage}
                                imageFileEncodedQ={imageFileEncodedQ}
                                handleFileChangeQ={handleFileChangeQ}
                                setImageFileEncodedQ={setImageFileEncodedQ}
                            />
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}
